import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { Plus, Trash } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import VatRateSelect from '../components/VatRateSelect';
import { toast } from 'sonner';
import { useConfirm } from '../components/ConfirmProvider';
import RecordLossButton from '../components/RecordLossButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ITProducts = () => {
  const confirm = useConfirm();
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: '',
    purchase_price: '',
    sale_price: '',
    stock_quantity: '',
    alert_threshold: '5',
    sku: '',
    vat_rate: 0.085,
    supplier: '',
  });

  useEffect(() => {
    fetchProducts();
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/suppliers`, { withCredentials: true });
      setSuppliers(r.data);
    } catch (e) { /* ignore */ }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/it-products`, {
        withCredentials: true,
      });
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Erreur lors du chargement des produits');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        purchase_price: parseFloat(formData.purchase_price),
        sale_price: parseFloat(formData.sale_price),
        stock_quantity: parseInt(formData.stock_quantity),
        alert_threshold: parseInt(formData.alert_threshold)
      };

      if (editingProduct) {
        await axios.put(`${API_URL}/api/it-products/${editingProduct.id}`, payload, { withCredentials: true });
        toast.success('Produit mis à jour');
      } else {
        await axios.post(`${API_URL}/api/it-products`, payload, { withCredentials: true });
        toast.success('Produit créé');
      }
      setShowDialog(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirm('Êtes-vous sûr de vouloir supprimer ce produit ?'))) return;
    try {
      await axios.delete(`${API_URL}/api/it-products/${id}`, { withCredentials: true });
      toast.success('Produit supprimé');
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: '',
      description: '',
      purchase_price: '',
      sale_price: '',
      stock_quantity: '',
      alert_threshold: '5',
      sku: '',
      vat_rate: 0.085,
      supplier: '',
    });
    setEditingProduct(null);
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      category: product.category,
      description: product.description,
      purchase_price: product.purchase_price.toString(),
      sale_price: product.sale_price.toString(),
      stock_quantity: product.stock_quantity.toString(),
      alert_threshold: product.alert_threshold.toString(),
      sku: product.sku,
      vat_rate: product.vat_rate !== undefined ? product.vat_rate : 0.085,
      supplier: product.supplier || '',
    });
    setShowDialog(true);
  };

  return (
    <div className="App">
      <Sidebar />
      <div className="main-content" data-testid="it-products-page">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2">Produits informatiques et électroniques</h1>
            <p className="text-slate-600">Gérez votre inventaire de produits IT et électroniques</p>
          </div>
          <Button
            onClick={() => { resetForm(); setShowDialog(true); }}
            data-testid="create-it-product-btn"
            className="bg-orange-500 hover:bg-orange-400 text-white"
          >
            <Plus size={20} weight="bold" className="mr-2" />
            Ajouter un produit
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">Chargement...</div>
        ) : (
          <>
            <div className="mb-4">
              <input
                type="text"
                placeholder="Rechercher par nom, catégorie ou SKU..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-it-products-input"
                className="w-full md:w-96 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">SKU</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Nom</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Catégorie</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Prix achat</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Prix vente</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Stock</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Fournisseur</th>
                    <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products
                    .filter(p => !searchQuery || 
                      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      p.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      (p.sku && p.sku.toLowerCase().includes(searchQuery.toLowerCase()))
                    )
                    .map((product) => (
                    <tr key={product.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4 text-sm text-slate-600">{product.sku || '-'}</td>
                      <td className="py-3 px-4 text-sm text-slate-900 font-medium">{product.name}</td>
                      <td className="py-3 px-4 text-sm text-slate-600">{product.category}</td>
                      <td className="py-3 px-4 text-sm text-slate-900">{product.purchase_price.toFixed(2)} €</td>
                      <td className="py-3 px-4 text-sm text-slate-900">{product.sale_price.toFixed(2)} €</td>
                      <td className="py-3 px-4 text-sm">
                        <span className={product.stock_quantity <= product.alert_threshold ? 'inline-block px-2 py-1 rounded-full text-xs bg-red-100 text-red-700 font-medium' : 'text-slate-900'}>
                          {product.stock_quantity}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">{product.supplier || '-'}</td>
                      <td className="py-3 px-4 text-right">
                        <button
                          onClick={() => openEditDialog(product)}
                          data-testid={`edit-it-product-${product.id}`}
                          className="text-blue-600 hover:text-blue-800 text-sm mr-2"
                        >
                          Modifier
                        </button>
                        <RecordLossButton
                          productId={product.id}
                          productType="it_product"
                          productName={product.name}
                          currentStock={product.stock_quantity}
                          onDone={fetchProducts}
                        />
                        <button
                          onClick={() => handleDelete(product.id)}
                          data-testid={`delete-it-product-${product.id}`}
                          className="text-red-600 hover:text-red-800 text-sm ml-2"
                        >
                          Supprimer
                        </button>
                      </td>
                    </tr>
                  ))}
                  {products.length === 0 && (
                    <tr>
                      <td colSpan="8" className="py-12 text-center text-slate-500">
                        Aucun produit enregistré
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          </>
        )}

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent data-testid="it-product-dialog" className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingProduct ? 'Modifier' : 'Ajouter'} un produit</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Nom</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    data-testid="it-product-name-input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Catégorie</label>
                  <Input
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    data-testid="it-product-category-input"
                    placeholder="Souris, Clavier, Écran..."
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">SKU (optionnel)</label>
                <Input
                  value={formData.sku}
                  onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                  data-testid="it-product-sku-input"
                  placeholder="CODE-001"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  data-testid="it-product-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Prix d'achat (€)</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.purchase_price}
                    onChange={(e) => setFormData({ ...formData, purchase_price: e.target.value })}
                    data-testid="it-product-purchase-price-input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Prix de vente (€)</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.sale_price}
                    onChange={(e) => setFormData({ ...formData, sale_price: e.target.value })}
                    data-testid="it-product-sale-price-input"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Quantité en stock</label>
                  <Input
                    type="number"
                    value={formData.stock_quantity}
                    onChange={(e) => setFormData({ ...formData, stock_quantity: e.target.value })}
                    data-testid="it-product-stock-input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Seuil d'alerte</label>
                  <Input
                    type="number"
                    value={formData.alert_threshold}
                    onChange={(e) => setFormData({ ...formData, alert_threshold: e.target.value })}
                    data-testid="it-product-alert-input"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Taux de TVA</label>
                <VatRateSelect
                  value={formData.vat_rate}
                  onChange={(v) => setFormData({ ...formData, vat_rate: v })}
                  testId="it-product-vat-rate-select"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Fournisseur</label>
                <select
                  value={formData.supplier}
                  onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                  data-testid="it-product-supplier-input"
                  className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white"
                >
                  <option value="">— Aucun —</option>
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.name}>{s.name}</option>
                  ))}
                  {formData.supplier && !suppliers.find((s) => s.name === formData.supplier) && (
                    <option value={formData.supplier}>{formData.supplier} (ancien)</option>
                  )}
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  Vous pouvez ajouter de nouveaux fournisseurs dans l&apos;onglet <strong>Fournisseurs</strong>.
                </p>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowDialog(false)}
                  data-testid="cancel-it-product-btn"
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  data-testid="submit-it-product-btn"
                  className="bg-blue-600 hover:bg-blue-500 text-white"
                >
                  {editingProduct ? 'Mettre à jour' : 'Créer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default ITProducts
